import React from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const InvestmentInsights = ({ commune }) => {
  if (!commune) return null;

  const COLORS = ['#B8860B', '#CD853F', '#FF6B35', '#27AE60', '#3498DB'];

  const investmentMetrics = [
  {
    icon: 'TrendingUp',
    label: 'Appréciation Annuelle',
    value: commune?.investment?.appreciation,
    change: '+2.3%',
    positive: true
  },
  {
    icon: 'DollarSign',
    label: 'Rendement Locatif',
    value: commune?.investment?.rentalYield,
    change: '+0.5%',
    positive: true
  },
  {
    icon: 'Clock',
    label: 'Temps de Vente Moyen',
    value: commune?.investment?.averageSaleTime,
    change: '-15 jours',
    positive: true
  },
  {
    icon: 'Target',
    label: 'Demande Locative',
    value: commune?.investment?.rentalDemand,
    change: 'Élevée',
    positive: true
  }];


  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-16 border-none">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="BarChart3" size={20} className="text-brand-gold" />
            <span className="text-sm font-medium text-brand-gold uppercase tracking-wide">
              Analyse d'Investissement
            </span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-4">
            Potentiel d'Investissement
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Données de marché et tendances pour éclairer vos décisions d'investissement immobilier
          </p>
        </div>

        {/* Investment Metrics */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 border-none">
          {investmentMetrics?.map((metric, index) =>
          <div key={index} className="bg-surface rounded-xl p-6 border border-border hover:shadow-warm transition-shadow duration-300 border-none">
              <div className="flex items-center justify-between mb-4">
                <div className="p-2 bg-brand-gold/10 rounded-lg">
                  <Icon name={metric?.icon} size={20} className="text-brand-gold" />
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
              metric?.positive ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`
              }>
                  {metric?.change}
                </span>
              </div>
              
              <h3 className="text-2xl font-bold text-text-primary mb-1">
                {metric?.value}
              </h3>
              <p className="text-sm text-text-secondary">{metric?.label}</p>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-12 border-none">
          {/* Price Evolution Chart */}
          <div className="bg-surface rounded-xl p-6 border border-border border-none">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-1">
                  Évolution des Prix
                </h3>
                <p className="text-sm text-text-secondary">
                  Prix moyen au m² sur 5 ans
                </p>
              </div>
              <Icon name="TrendingUp" size={20} className="text-brand-gold" />
            </div>
            
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={commune?.investment?.priceEvolution}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis
                    dataKey="year"
                    stroke="#5D6D7E"
                    fontSize={12} />

                  <YAxis
                    stroke="#5D6D7E"
                    fontSize={12}
                    tickFormatter={(value) => `${value}k`} />

                  <Tooltip
                    formatter={(value) => [`${value}k FCFA`, 'Prix/m²']}
                    labelStyle={{ color: '#2C3E50' }} />

                  <Line
                    type="monotone"
                    dataKey="price"
                    stroke="#B8860B"
                    strokeWidth={3}
                    dot={{ fill: '#B8860B', strokeWidth: 2, r: 4 }} />

                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Property Types Distribution */}
          <div className="bg-surface rounded-xl p-6 border border-border border-none">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-1">
                  Types de Biens
                </h3>
                <p className="text-sm text-text-secondary">
                  Répartition du marché immobilier
                </p>
              </div>
              <Icon name="PieChart" size={20} className="text-brand-gold" />
            </div>
            
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={commune?.investment?.propertyTypes}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value">

                    {commune?.investment?.propertyTypes?.map((entry, index) =>
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                    )}
                  </Pie>
                  <Tooltip formatter={(value) => `${value}%`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mt-4">
              {commune?.investment?.propertyTypes?.map((type, index) =>
              <div key={index} className="flex items-center space-x-2">
                  <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: COLORS?.[index % COLORS?.length] }} />

                  <span className="text-sm text-text-secondary">{type?.name}</span>
                  <span className="text-sm font-medium text-text-primary">{type?.value}%</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Investment Opportunities */}
        <div className="mt-12 bg-gradient-to-br from-brand-gold/5 to-brand-earth/5 rounded-2xl p-8 border-brand-gold/20 border-none border-0">
          <div className="flex items-center space-x-2 mb-6">
            <Icon name="Lightbulb" size={20} className="text-brand-gold" />
            <h3 className="text-xl font-heading font-semibold text-text-primary">
              Opportunités d'Investissement
            </h3>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {commune?.investment?.opportunities?.map((opportunity, index) =>
            <div key={index} className="bg-white rounded-lg p-6 shadow-warm">
                <div className="flex items-center space-x-2 mb-3">
                  <Icon name="Star" size={16} className="text-brand-gold" />
                  <span className="text-sm font-medium text-brand-gold">
                    {opportunity?.type}
                  </span>
                </div>
                
                <h4 className="font-semibold text-text-primary mb-2">
                  {opportunity?.title}
                </h4>
                
                <p className="text-sm text-text-secondary mb-4">
                  {opportunity?.description}
                </p>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-brand-gold">
                    ROI Estimé: {opportunity?.estimatedROI}
                  </span>
                  <Icon name="ArrowRight" size={16} className="text-text-secondary" />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>);

};

export default InvestmentInsights;